import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";

const ApiServices = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetch("/APIServicesPlan.json");
                if (!response.ok) {
                    throw new Error("Failed to fetch plans");
                }
                const jsonData = await response.json();
                setPlans(jsonData);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
                    <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Plans</h2>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-6 rounded-full transition duration-300"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-16">
                    <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
                        NextEra Research API Bridge Service
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                        Seamless integration for your trading and research needs
                    </p>
                </div>

                {/* API Bridge Description Section */}
                <div className="flex flex-col lg:flex-row gap-12 mb-20">
                    <div className="lg:w-1/2">
                        <div className="bg-white p-8 rounded-xl shadow-md">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">
                                About Our API Bridge Service
                            </h2>
                            <div className="space-y-4 text-gray-600">
                                <p>
                                    In today's fast-paced financial markets, seamless communication between trading platforms and research tools is essential. Our API Bridge service provides this critical connection, enabling real-time data exchange between your systems and our research analytics.
                                </p>
                                <p>
                                    As a dedicated research analytics, we offer this integration capability across all our services - Options, Cash, Futures, and MCX. The API Bridge gives you flexible configuration options with both manual and automatic modes to suit your trading style.
                                </p>
                                <p>
                                    Customize your integration with position sizing based on your investment profile and other parameters. Please note this is a delivery mechanism only - all trading calls originate from our research analysts based on market conditions.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="lg:w-1/2 flex items-center justify-center">
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 w-full max-w-md">
                            <img
                                src="/image2.png"
                                alt="API Bridge Architecture"
                                className="w-full h-auto rounded-lg object-cover"
                            />
                            <div className="mt-6 text-center">
                                <h3 className="text-lg font-medium text-gray-800">Seamless Integration</h3>
                                <p className="mt-2 text-gray-600">
                                    Connect your trading platform with our research signals in minutes
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full mb-20">
                    {/* Section Heading */}
                    <div className="text-center mb-12">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                            Manual Trading vs Algo Trading
                        </h2>
                        <p className="mt-4 text-gray-600 max-w-3xl mx-auto">
                            Understand the clear difference between human-driven trading and
                            rule-based algorithmic trading through real-world comparisons.
                        </p>
                    </div>

                    {/* Image Cards */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                        {/* Card 1 */}
                        <div className="bg-white rounded-xl  overflow-hidden">
                            <img
                                src="/image.png"
                                alt="Manual vs Algo Trading Comparison Table"
                                className="w-auto rounded-t-xl h-auto object-cover "
                            />

                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                    Side-by-Side Comparison
                                </h3>
                                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                                    This comparison highlights how manual trading relies heavily on
                                    human judgment, emotions, and experience, often leading to
                                    inconsistency. In contrast, algorithmic trading executes trades
                                    based on predefined rules with speed, precision, and discipline,
                                    ensuring consistency and controlled risk across all market
                                    conditions.
                                </p>
                            </div>
                        </div>

                        {/* Card 2 */}
                        <div className="bg-white rounded-xl  overflow-hidden">
                            <img
                                src="/image1.png"
                                alt="Manual Trading Pain Points vs Algo Trading Benefits"
                                className="w-auto h-auto rounded-t-xl object-cover"
                            />

                            <div className="p-6">
                                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                    Pain Points vs Gain Points
                                </h3>
                                <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                                    Manual trading often struggles with emotional decision-making,
                                    screen fatigue, and lack of proper backtesting. Algo trading removes
                                    these limitations by offering emotion-free execution, strong risk
                                    management, back-tested strategies, and scalable systems that can
                                    trade multiple instruments simultaneously.
                                </p>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Advanced Research Solutions Section */}
                <div className="mb-20">
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className="p-8 md:p-10">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">
                                Advanced Research Solutions
                            </h2>
                            <h3 className="text-xl font-semibold text-gray-700 mb-4">
                                Powerful API Bridge Technology for Modern Trading
                            </h3>

                            <div className="grid md:grid-cols-2 gap-8 mt-8">
                                <div>
                                    <p className="text-gray-600 mb-6">
                                        An API bridge in trading is a software interface that connects different systems involved in the trading process, such as trading platforms, algorithmic trading software, and brokers. It facilitates automated trading, real-time data analysis, and efficient execution of trading signals.
                                    </p>
                                    <p className="text-gray-600">
                                        API bridges allow for seamless integration and communication between these systems, enabling traders to automate their strategies and execute trades with speed and accuracy.
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <h4 className="font-bold text-gray-700 mb-4">Key Features:</h4>
                                    <ul className="space-y-3">
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                                    <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className="ml-3 text-gray-600">
                                                <strong>Connecting Systems:</strong> Acts as a bridge between trading platforms, algorithmic trading software, and brokers
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                                    <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className="ml-3 text-gray-600">
                                                <strong>Automated Trading:</strong> Enables execution of research signals without manual intervention
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                                    <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className="ml-3 text-gray-600">
                                                <strong>Real-time Data Analysis:</strong> Provides access to live market data for informed decision making
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                                    <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className="ml-3 text-gray-600">
                                                <strong>Efficient Execution:</strong> Reduces latency and improves order placement speed
                                            </span>
                                        </li>
                                        <li className="flex items-start">
                                            <div className="flex-shrink-0 mt-1">
                                                <div className="h-5 w-5 bg-green-100 rounded-full flex items-center justify-center">
                                                    <svg className="h-3 w-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <span className="ml-3 text-gray-600">
                                                <strong>Integration:</strong> Ensures accurate and efficient data/order transmission between systems
                                            </span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pricing Plans */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-12">
                        Choose Your API Plan
                    </h2>

                    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                        {plans.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative rounded-xl shadow-md overflow-hidden border transition-all duration-300 hover:shadow-lg hover:transform hover:-translate-y-2 
                                    ${index === 1 ? "border-green-500 ring-2 ring-green-200" : "border-gray-200"}`}
                            >
                                {index === 1 && (
                                    <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 transform rotate-12 translate-x-2 -translate-y-1">
                                        Popular
                                    </div>
                                )}

                                <div className="p-6 h-full flex flex-col">
                                    <h3 className="text-lg font-bold text-white p-3 bg-green-500 rounded-lg text-center">
                                        {plan.title}
                                    </h3>
                                    <p className="text-2xl font-bold text-gray-800 mt-4 text-center">
                                        {plan.price}
                                    </p>

                                    <ul className="mt-6 space-y-3 flex-grow">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="flex items-start">
                                                <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span className="text-gray-600">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <div className="mt-8 text-center">
                                        <button
                                            onClick={() => navigate(`/payment`)}
                                            className={`w-full py-3 px-6 rounded-lg font-medium transition duration-300
                                                ${index === 1 ? "bg-green-600 hover:bg-green-700 text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"}`}
                                        >
                                            Pay Now
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* CTA Section */}
                <div className="bg-green-50 rounded-xl p-8 text-center border border-green-100">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Need a custom solution?</h3>
                    <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                        Our team can create a tailored API integration to meet your specific requirements and trading volume.
                    </p>
                    <button onClick={() => navigate('/contactus')} className="bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-8 rounded-lg transition duration-300 inline-flex items-center">
                        Contact Us <FaArrowRight />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApiServices;